import React, { memo, useMemo, useState } from 'react'
import { ExtraSmallButton } from '../../../../custom'
import { ListItemIcon, ListItemText, MenuItem, Popover, Typography } from '@mui/material'
import Line from '../../../../../assets/icons/chartTypes/line'
import CurvedLine from '../../../../../assets/icons/plotTypes/curvedLine'
import StepLine from '../../../../../assets/icons/plotTypes/stepLine'
import Cross from '../../../../../assets/icons/plotTypes/cross'
import Circles from '../../../../../assets/icons/plotTypes/circles'
import AnalyticsManager from '../../../../../analytics/AnalyticsManager'
import { LineType } from 'lightweight-charts'
import { PlotTypesMenuMultiStrikeChart } from '../../../chart.typings'
import { useWidgetSettingsMenuPlacement } from '../../../../widgetDashboard/widgetSettingsMenuContext'

type Props = {
    lineType: LineType,
    onSelected: (menuItem: LineType) => void
}

export const getPlotTypeIcon = (lineType: LineType) => {
    if (lineType === LineType.Simple) {
        return <Line />
    }
    if (lineType === LineType.Curved) {
        return <CurvedLine />
    }
    if (lineType === LineType.WithSteps) {
        return <StepLine />
    }
    if (lineType === LineType.Cross) {
        return <Cross />
    }
    if (lineType === LineType.Circle) {
        return <Circles />
    }
    return null
}

const OutputPlotTypesView = ({ lineType, onSelected }: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const menuPlacement = useWidgetSettingsMenuPlacement('left')

    const selectedItem = useMemo(() => {
        return PlotTypesMenuMultiStrikeChart.find((menuItem) => menuItem.type === lineType)
    }, [lineType])

    const plotTypeIcon = useMemo(() => getPlotTypeIcon(lineType), [lineType])

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const onChange = (menuItem: LineType) => {
        onSelected(menuItem)
    }

    return (<>
        <ExtraSmallButton
            onClick={handleClick}
            variant="outlined"
            color="inherit"
            style={{
                padding: '2px',
                height: '34px',
                minHeight: '34px',
                minWidth: '34px',
                width: plotTypeIcon ? '34px' : 'auto',
                boxSizing: 'border-box',
                lineHeight: 1,
            }}
            sx={{
                borderRadius: 1,
                height: '34px !important',
                minHeight: '34px !important',
                minWidth: '34px !important',
                '& svg': {
                    width: 28,
                    height: 28,
                    display: 'block',
                },
            }}
        >
            {plotTypeIcon ? plotTypeIcon : <Typography sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 0.5
            }}> {selectedItem?.label ?? ''}</Typography>}
        </ExtraSmallButton>
        <Popover
            elevation={3}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            disablePortal={menuPlacement.disablePortal}
            container={menuPlacement.container}
            anchorOrigin={menuPlacement.anchorOrigin}
            transformOrigin={menuPlacement.transformOrigin}
            sx={{ overflowY: 'auto', ...menuPlacement.overlaySx }}
        >
            {
                PlotTypesMenuMultiStrikeChart.map((menuItem) => {
                    return <MenuItem selected={menuItem.type === lineType} key={menuItem.label} onClick={() => {
                        AnalyticsManager.logEvent('Setting Style Changed', { source: 'multistrike', type: 'line' })
                        onChange(menuItem.type)
                        setAnchorEl(null)
                    }}>
                        <ListItemIcon>
                            {getPlotTypeIcon(menuItem.type)}
                        </ListItemIcon>
                        <ListItemText>{menuItem.label}</ListItemText>
                    </MenuItem>
                })
            }
        </Popover>
    </>)
}

export default memo(OutputPlotTypesView)